import { Component, computed, input, signal } from '@angular/core';
import {
  ContractorDocument,
  ContractorDocumentType,
  CONTRACTOR_DOCUMENT_TYPE_RECORD
} from '@umss/shared/data-access';
import { UmssCardHeaderComponent } from '../card/card-header/card-header.component';
import { UmssSkeletonComponent } from '../skeleton/skeleton.component';
import { UmssEmptyStateComponent } from '../empty-state/empty-state.component';
import { UmssButtonComponent } from '../button/button.component';
import { UmssContractorDocumentTypeSelectComponent } from '../contractor-document-type-select/contractor-document-type-select.component';
import { UmssDocumentThumbnailComponent } from '../document-thumbnail/document-thumbnail.component';

/**
 * Grid of a contractor's documents — thumbnail, name, type and a View action.
 *
 * Presentational only: the parent fetches and supplies `documents`. It reads the
 * same shape in every app, which is what lets the business and facility surfaces
 * render identically instead of maintaining two lookalike lists.
 *
 * The type filter is deliberately internal — it is view state, not something a
 * parent needs to own.
 */
@Component({
  selector: 'umss-contractor-documents-card',
  standalone: true,
  imports: [
    UmssCardHeaderComponent,
    UmssSkeletonComponent,
    UmssEmptyStateComponent,
    UmssButtonComponent,
    UmssContractorDocumentTypeSelectComponent,
    UmssDocumentThumbnailComponent
  ],
  templateUrl: './contractor-documents-card.component.html',
  styleUrls: ['./contractor-documents-card.component.scss']
})
export class UmssContractorDocumentsCardComponent {
  /** Every document for the contractor; the type filter is applied here. */
  documents = input<ContractorDocument[]>([]);

  loading = input(false);

  /**
   * Whether to show the document type filter dropdown. Disabled in compact
   * contexts such as the contractor profile modal.
   */
  showFilter = input(true);

  /**
   * When true, the whole document card is clickable to open the document and
   * the trailing "View" button is hidden. Used in the profile modal.
   */
  clickableCards = input(false);

  selectedDocumentType = signal<ContractorDocumentType | null>(null);

  protected readonly visibleDocuments = computed(() => {
    const all = this.documents();
    const typeFilter = this.selectedDocumentType();
    if (!typeFilter) return all;
    return all.filter((doc) => doc.documentType === typeFilter);
  });

  /**
   * Shows document type subtitle only when no filter is applied.
   * When filtering by a specific type, the type is already evident from the filter.
   */
  protected readonly showTypeSubtitle = computed(
    () => !this.showFilter() || this.selectedDocumentType() === null
  );

  protected openDocument(url: string): void {
    if (url) {
      window.open(url, '_blank', 'noopener');
    }
  }

  protected getDocumentTypeLabel(documentType: ContractorDocumentType): string {
    return CONTRACTOR_DOCUMENT_TYPE_RECORD[documentType] || documentType;
  }
}
